import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, computed, signal } from '@angular/core';
import { Subject, map, takeUntil } from 'rxjs';
import { formatDistanceToNow } from 'date-fns';
import { DesignHistoryEntry, DesignSaveFailure, EndClientReport, EndClientReportTemplate } from '../../../models/end-client-reporting-package';
import { EndClientReportService } from '../end-client-report.service';
import { parseUtcDate } from '../../../helpers/date.helper';

/**
 * NK-344 — a deck's design history, and restoring from it.
 *
 * Every version, newest first, with what changed, who did it and when. The point of the list is
 * choosing: a bare "Version 1 … Version 12" picker is what this replaces.
 *
 * Restoring writes a **new** version holding the old content rather than rewinding the counter, so
 * a revert appears in this list as its own entry. That keeps the history monotonic and means
 * reverting a revert is just another restore, with nothing special about it.
 */
/** About two lines of a summary at the history popup's width, past which it is cut and offered whole. */
const LONG_SUMMARY_CHARS = 150;

@Component({
  selector: 'or-report-history',
  templateUrl: './report-history.component.html',
  styleUrls: ['./report-history.component.scss'],
  standalone: false
})
export class ReportHistoryComponent implements OnInit, OnDestroy {
  /** The deck whose history this is. Leave empty and set `templateId` for a template's. */
  @Input() reportId = '';

  /**
   * NK-450: a template's history instead of a deck's. Same list, same restore -- only the endpoints
   * and what a restore hands back differ.
   */
  @Input() templateId = '';

  /** Set while manual subsection editing is on — restoring would fight the document being edited. */
  @Input() disabled = false;

  /**
   * The version the report or template is on now. The list is re-read whenever it moves, so a save
   * made while the panel is open -- a manual edit, an AI edit, a restore -- shows up without closing
   * and reopening it. The first value only records where it started; ngOnInit does the first load.
   */
  @Input() set version(value: number) {
    const changed = this.knownVersion !== null && value !== this.knownVersion;
    this.knownVersion = value;
    if (changed) this.load();
  }
  private knownVersion: number | null = null;

  /**
   * Its own heading and close button. Off inside the history popup, whose header carries both.
   */
  @Input() showHeader = true;

  /**
   * Emitted after a successful restore, carrying the report as it now stands. The parent owns
   * reloading the design and holding the new version; this never mutates either.
   */
  @Output() designReverted = new EventEmitter<EndClientReport>();

  /** The template counterpart of `designReverted`. */
  @Output() templateReverted = new EventEmitter<EndClientReportTemplate>();

  /**
   * Asked to close. The panel is opened from a menu that only shows a check mark, so it needs a way
   * out of its own -- the parent owns whether it's open, so this reports rather than decides.
   */
  @Output() closed = new EventEmitter<void>();

  entries = signal<DesignHistoryEntry[]>([]);

  /** A version is live on a share link (NK-450), so a restore needs saying it won't reach it. */
  published = computed(() => this.entries().some(entry => entry.isLive));
  loading = signal(true);
  loadFailed = signal(false);

  /** The versions whose whole summary is showing, not just its first two lines. */
  private expanded = signal<ReadonlySet<number>>(new Set());

  /** The version a restore is in flight for, so only its own row shows a spinner. */
  reverting = signal<number | null>(null);
  revertFailure = signal<DesignSaveFailure | null>(null);

  private destroy$ = new Subject<void>();

  constructor(private reportService: EndClientReportService) { }

  ngOnInit() {
    this.load();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * What to show as the change. The summary where there is one — the agent's own words for a chat
   * edit, a computed diff for a hand save — and the source's label where a save changed nothing we
   * could identify. Never a made-up description of an unidentified change.
   */
  changeLabel(entry: DesignHistoryEntry): string {
    return entry.summary?.trim() || entry.sourceLabel;
  }

  /**
   * Only shown when it adds something the summary doesn't: what was asked for, as opposed to what
   * changed. Suppressed when they're effectively the same line.
   */
  askedFor(entry: DesignHistoryEntry): string | null {
    const message = entry.userMessage?.trim();
    if (!message) return null;
    return message === entry.summary?.trim() ? null : message;
  }

  /**
   * Whether a summary is long enough to be cut at two lines, so the row offers the rest. Judged by
   * length, not measured: about two lines of the popup's width, and a More under a summary that was
   * never cut would be a button that does nothing.
   */
  isLong(entry: DesignHistoryEntry): boolean {
    return (entry.summary?.trim().length ?? 0) > LONG_SUMMARY_CHARS;
  }

  isExpanded(entry: DesignHistoryEntry): boolean {
    return this.expanded().has(entry.version);
  }

  toggleExpanded(entry: DesignHistoryEntry) {
    const next = new Set(this.expanded());
    if (next.has(entry.version)) next.delete(entry.version);
    else next.add(entry.version);
    this.expanded.set(next);
  }

  savedByLabel(entry: DesignHistoryEntry): string {
    // Not "Unknown": nobody edited it. A machine wrote this version, and saying so is more useful
    // than implying we lost the name.
    return entry.savedByName?.trim() || 'Navigator';
  }

  // parseUtcDate, not parseISO: these timestamps are stored in UTC but round-trip out of MySQL as
  // DateTime.Kind = Unspecified, so the JSON has no trailing `Z` and parseISO reads it as local
  // time -- putting every entry out by the viewer's own offset.
  savedAtLabel(entry: DesignHistoryEntry): string {
    try {
      return `${formatDistanceToNow(parseUtcDate(entry.savedAt))} ago`;
    } catch {
      return '';
    }
  }

  canRevert(entry: DesignHistoryEntry): boolean {
    return !entry.isCurrent && !this.disabled && this.reverting() === null;
  }

  revert(entry: DesignHistoryEntry) {
    if (!this.canRevert(entry)) return;
    this.reverting.set(entry.version);
    this.revertFailure.set(null);

    // Each branch reports through its own output, since a deck and a template come back as
    // different shapes and the parent re-reads each from its own endpoint.
    const revert = this.templateId
      ? this.reportService.revertTemplate(this.templateId, entry.version)
        .pipe(map(template => () => this.templateReverted.emit(template)))
      : this.reportService.revertDesign(this.reportId, entry.version)
        .pipe(map(report => () => this.designReverted.emit(report)));

    revert
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: emit => {
          this.reverting.set(null);
          // No reload here: the restore added a version of its own, and the parent taking the
          // returned report or template moves `version`, which re-reads the list.
          emit();
        },
        error: (failure: DesignSaveFailure) => {
          this.reverting.set(null);
          this.revertFailure.set(failure);
        },
      });
  }

  /** Same failure vocabulary as a normal save, because a revert goes through the same path. */
  failureMessage(failure: DesignSaveFailure): string {
    switch (failure) {
      case 'Busy':
        return 'Someone else is saving this report right now. Try again in a moment.';
      case 'VersionConflict':
        return 'This report changed while the restore was starting. Reload it and try again.';
      case 'Invalid':
        return "That version isn't valid against today's rules, so it wasn't restored.";
      default:
        return "Couldn't restore that version. Nothing was changed.";
    }
  }

  private load() {
    this.loading.set(true);
    const history = this.templateId
      ? this.reportService.getTemplateHistory(this.templateId)
      : this.reportService.getDesignHistory(this.reportId);
    history
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: entries => {
          this.entries.set(entries);
          this.loading.set(false);
          this.loadFailed.set(false);
        },
        error: () => {
          this.loadFailed.set(true);
          this.loading.set(false);
        },
      });
  }
}
